import React, { useState } from "react";
import {
	Box,
	Grid,
	Typography,
	TextField,
	Button,
	Alert,
	CircularProgress,
	Tabs,
	Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import PaymentsTable from "components/Tables/PaymentsTable";
import { useAdminRentalPayments } from "services/hooks/rentals";

const TitleContainer = styled(Grid)(({ theme }) => ({
	marginBottom: theme.spacing(2),
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
}));

export default function Payments() {
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(20);
	const [searchInput, setSearchInput] = useState("");
	const [appliedSearch, setAppliedSearch] = useState("");
	// "" = all payments, "anonymous" = tenants of anonymous landlord tenancies
	const [tenancy, setTenancy] = useState("");

	const { payments, pagination, loading, error } = useAdminRentalPayments({
		page: page + 1,
		pageSize: rowsPerPage,
		search: appliedSearch,
		tenancy,
	});

	const handleTenancyChange = (_event, value) => {
		setTenancy(value);
		setPage(0);
	};

	const applySearch = () => {
		setAppliedSearch(searchInput.trim());
		setPage(0);
	};

	const handleSearchKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			applySearch();
		}
	};

	const handleClearSearch = () => {
		setSearchInput("");
		setAppliedSearch("");
		setPage(0);
	};

	const handleChangePage = (_event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	return (
		<Box m="20px">
			<Grid container spacing={2} style={{ marginTop: "20px" }}>
				<Grid item xs={12}>
					<TitleContainer>
						<Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "25px" }}>
							Payments
						</Typography>
					</TitleContainer>
				</Grid>
			</Grid>

			<Tabs value={tenancy} onChange={handleTenancyChange} sx={{ mb: 2 }}>
				<Tab label="All Payments" value="" />
				<Tab label="Anonymous Tenants" value="anonymous" />
			</Tabs>

			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				{tenancy === "anonymous"
					? "Successful payments by tenants under anonymous landlord tenancies, newest first"
					: "Successful rental payments, newest first"}
				{pagination.total > 0
					? ` — ${pagination.total} payment${pagination.total === 1 ? "" : "s"} total.`
					: "."}
			</Typography>

			<Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
				<TextField
					size="small"
					label="Search by tenant name or RB reference"
					value={searchInput}
					onChange={(e) => setSearchInput(e.target.value)}
					onKeyDown={handleSearchKeyDown}
					sx={{ minWidth: 260, flex: "1 1 240px", maxWidth: 420 }}
				/>
				<Button variant="contained" startIcon={<SearchIcon />} onClick={applySearch}>
					Search
				</Button>
				{appliedSearch ? (
					<Button variant="text" onClick={handleClearSearch}>
						Clear
					</Button>
				) : null}
			</Box>

			{loading && (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress />
				</Box>
			)}

			{error && !loading && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{typeof error === "string" ? error : "Error loading payments."}
				</Alert>
			)}

			{!loading && !error && (
				<PaymentsTable
					rows={payments}
					count={pagination.total ?? 0}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			)}
		</Box>
	);
}
